import chalk from 'chalk';
import ora from 'ora';
import { readFileSync, existsSync } from 'node:fs';
import { join, resolve } from 'node:path';
import {
  lintCharter,
  lintTools,
  lintAll,
  type LintDiagnostic,
  type LintResult,
  type OrgChannelPolicy,
  type ChannelId,
} from '@augmented/core';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, warn, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

function printDiagnostics(diagnostics: LintDiagnostic[]): void {
  for (const d of diagnostics) {
    const prefix = d.severity === 'error' ? chalk.red('ERR') : chalk.yellow('WARN');
    const file = chalk.dim(`[${d.file}]`);
    const path = d.path ? chalk.dim(` (${d.path})`) : '';
    console.log(`  ${prefix} ${file} ${d.code}: ${d.message}${path}`);
  }
}

function printResult(label: string, result: LintResult): void {
  if (result.ok && result.warnings.length === 0) {
    success(`${label}: passed`);
    return;
  }
  if (result.errors.length > 0) {
    error(`${label}: ${result.errors.length} error(s)`);
    printDiagnostics(result.errors);
  }
  if (result.warnings.length > 0) {
    warn(`${label}: ${result.warnings.length} warning(s)`);
    printDiagnostics(result.warnings);
  }
}

/**
 * `agt lint [path]`
 */
export async function lintCommand(path?: string): Promise<void> {
  const json = isJsonMode();

  const dirs: { name: string; dir: string }[] = [];

  if (path) {
    const resolved = resolve(path);
    if (!existsSync(resolved)) {
      if (json) {
        jsonOutput({ ok: false, error: `Path not found: ${resolved}` });
      } else {
        error(`Path not found: ${resolved}`);
      }
      process.exitCode = 1;
      return;
    }
    dirs.push({ name: path, dir: resolved });
  } else {
    const augmentedDir = join(process.cwd(), '.augmented');
    if (!existsSync(augmentedDir)) {
      if (json) {
        jsonOutput({ ok: false, error: 'No .augmented/ directory found' });
      } else {
        error('No .augmented/ directory found. Run `agt init` first.');
      }
      process.exitCode = 1;
      return;
    }

    const { readdirSync, statSync } = await import('node:fs');
    const entries = readdirSync(augmentedDir);
    for (const entry of entries) {
      const entryPath = join(augmentedDir, entry);
      if (statSync(entryPath).isDirectory()) {
        dirs.push({ name: entry, dir: entryPath });
      }
    }

    if (dirs.length === 0) {
      if (json) {
        jsonOutput({ ok: false, error: 'No agent directories found in .augmented/' });
      } else {
        error('No agent directories found in .augmented/. Run `agt init` first.');
      }
      process.exitCode = 1;
      return;
    }
  }

  // Optionally fetch team channel policy via API
  let orgPolicy: OrgChannelPolicy | undefined;
  const teamSlug = requireTeam();
  if (teamSlug) {
    const spinner = ora({ text: 'Fetching org channel policy…', isSilent: json });
    spinner.start();
    try {
      const data = await api.get<{
        channel_policy: {
          team_id: string;
          allowed_channels: string[];
          denied_channels: string[];
          require_elevated_for_pii: boolean;
        } | null;
      }>(`/teams/${encodeURIComponent(teamSlug)}/channel-policy`);

      if (data.channel_policy) {
        orgPolicy = {
          organization_id: data.channel_policy.team_id,
          allowed_channels: (data.channel_policy.allowed_channels ?? []) as ChannelId[],
          denied_channels: (data.channel_policy.denied_channels ?? []) as ChannelId[],
          require_elevated_for_pii: data.channel_policy.require_elevated_for_pii ?? false,
        };
      }
      spinner.stop();
    } catch {
      spinner.stop();
      // Non-fatal — lint without org policy
    }
  }

  let totalErrors = 0;
  let totalWarnings = 0;
  const jsonResults: Record<string, unknown>[] = [];

  for (const { name, dir } of dirs) {
    if (!json) console.log(chalk.bold(`\nLinting ${name}:`));

    const charterPath = join(dir, 'CHARTER.md');
    const toolsPath = join(dir, 'TOOLS.md');
    const hasCharter = existsSync(charterPath);
    const hasTools = existsSync(toolsPath);

    if (!hasCharter && !hasTools) {
      if (!json) warn('No CHARTER.md or TOOLS.md found — skipping.');
      if (json) jsonResults.push({ agent: name, skipped: true });
      continue;
    }

    const charterContent = hasCharter ? readFileSync(charterPath, 'utf-8') : undefined;
    const toolsContent = hasTools ? readFileSync(toolsPath, 'utf-8') : undefined;

    let result: LintResult;
    if (charterContent && toolsContent) {
      result = lintAll(charterContent, toolsContent, { orgChannelPolicy: orgPolicy });
      if (!json) printResult('Full lint', result);
    } else if (charterContent) {
      result = lintCharter(charterContent, { orgChannelPolicy: orgPolicy });
      if (!json) printResult('CHARTER.md', result);
    } else {
      result = lintTools(toolsContent!);
      if (!json) printResult('TOOLS.md', result);
    }

    totalErrors += result.errors.length;
    totalWarnings += result.warnings.length;

    if (json) {
      jsonResults.push({
        agent: name,
        ok: result.ok,
        errors: result.errors.length,
        warnings: result.warnings.length,
        diagnostics: [...result.errors, ...result.warnings],
      });
    }
  }

  if (json) {
    jsonOutput({
      ok: totalErrors === 0,
      agents: jsonResults,
      total_errors: totalErrors,
      total_warnings: totalWarnings,
    });
    if (totalErrors > 0) process.exitCode = 1;
    return;
  }

  console.log();
  if (totalErrors === 0 && totalWarnings === 0) {
    success(`All ${dirs.length} agent(s) passed lint.`);
  } else {
    if (totalErrors > 0) {
      error(`Total: ${totalErrors} error(s) across ${dirs.length} agent(s)`);
      process.exitCode = 1;
    }
    if (totalWarnings > 0) {
      warn(`Total: ${totalWarnings} warning(s) across ${dirs.length} agent(s)`);
    }
  }
}
